import { useCallback, useContext } from 'react';
import { AppStateContext } from './context';
import type { Event, Filter, TimeSlot, User, Itinerary, AmenityType, Amenity } from './types';

export const useAppState = () => {
    const context = useContext(AppStateContext);
    if (!context) throw new Error('useAppState must be used within AppStateProvider');
    return context;
};

export const useAppDispatchActions = () => {
    const { dispatch } = useAppState();

    const setActiveEvent = useCallback(
        (event: Event | null) => dispatch({ type: 'SET_ACTIVE_EVENT', payload: event }),
        [dispatch],
    );

    const setActiveAmenity = useCallback(
        (event: number | null) => dispatch({ type: 'SET_ACTIVE_AMENITY', payload: event }),
        [dispatch],
    );

    const setInterests = useCallback(
        (interests: Filter[]) => dispatch({ type: 'SET_INTERESTS', payload: interests }),
        [dispatch],
    );

    const setTimeSlot = useCallback(
        (timeSlot: TimeSlot) => dispatch({ type: 'SET_TIMESLOT', payload: timeSlot }),
        [dispatch],
    );

    const setActivityTypes = useCallback(
        (activityTypes: Filter[]) => dispatch({ type: 'SET_ACTIVITY_TYPE', payload: activityTypes }),
        [dispatch],
    );

    const setEvents = useCallback((events: Event[]) => dispatch({ type: 'SET_EVENTS', payload: events }), [dispatch]);

    const setFilteredEvents = useCallback(
        (events: Event[]) => dispatch({ type: 'SET_FILTERED_EVENTS', payload: events }),
        [dispatch],
    );

    const setSelectedInterests = useCallback(
        (interests: string[]) => dispatch({ type: 'SET_SELECTED_INTERESTS', payload: interests }),
        [dispatch],
    );

    const setSelectedActivityTypes = useCallback(
        (activityTypes: string[]) => dispatch({ type: 'SET_SELECTED_ACTIVITY_TYPES', payload: activityTypes }),
        [dispatch],
    );

    const setUser = useCallback((user: User | null) => dispatch({ type: 'SET_USER', payload: user }), [dispatch]);

    const setItinerary = useCallback(
        (itinerary: Itinerary | null) => dispatch({ type: 'SET_ITINERARY', payload: itinerary }),
        [dispatch],
    );

    /**
     * Opens authentication modal and sets state.
     * @param mode - 0: Close, 1: Register, 2: Login
     */
    const setIsRegistrationOpen = useCallback(
        (mode: 0 | 1 | 2) => dispatch({ type: 'SET_IS_REGISTRATION_OPEN', payload: mode }),
        [dispatch],
    );

    const setViewMode = useCallback(
        (viewMode: 'events' | 'itinerary') => dispatch({ type: 'SET_VIEW_MODE', payload: viewMode }),
        [dispatch],
    );

    const setMapExpanded = useCallback(
        (expanded: boolean) => dispatch({ type: 'SET_MAP_EXPANDED', payload: expanded }),
        [dispatch],
    );

    const setSplitRatio = useCallback(
        (ratio: number) => dispatch({ type: 'SET_SPLIT_RATIO', payload: ratio }),
        [dispatch],
    );
    const setAmenityTypes = useCallback(
        (amenityTypes: AmenityType[]) => dispatch({ type: 'SET_AMENITY_TYPES', payload: amenityTypes }),
        [dispatch],
    );
    const setSelectedAmenityTypes = useCallback(
        (selectedAmenityTypes: string[]) =>
            dispatch({ type: 'SET_SELECTED_AMENITY_TYPES', payload: selectedAmenityTypes }),
        [dispatch],
    );
    const setAmenities = useCallback(
        (amenities: Amenity[]) => dispatch({ type: 'SET_AMENITIES', payload: amenities }),
        [dispatch],
    );

    return {
        setActiveEvent,
        setActiveAmenity,
        setInterests,
        setTimeSlot,
        setActivityTypes,
        setEvents,
        setFilteredEvents,
        setSelectedInterests,
        setSelectedActivityTypes,
        setUser,
        setItinerary,
        setIsRegistrationOpen,
        setViewMode,
        setMapExpanded,
        setSplitRatio,
        setAmenityTypes,
        setSelectedAmenityTypes,
        setAmenities,
    };
};
